import classNames from "classnames";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Highlight } from "react-instantsearch";

import { useConfiguration } from "~/contexts/configuration";
import Algolia from "~/types/algolia";
import CrossCountry from "~/types/cross-country";
import gridUtils from "~/utils/grid-utils";
import imageUtils from "~/utils/image-utils";
import productUtils from "~/utils/product-utils";
import TrackingUtils from "~/utils/tracking-utils";

import buttonStyles from "../common/button.module.scss";
import Icon from "../common/icon";
import NextImage from "../common/next-image";
import ProductActions from "../common/product-actions";
import ProductListingTags from "./product-listing-tags";
import ProductPrice from "./product-price";
import ProductPriceAlgolia from "./product-price-algolia";
import ProductRatings from "./product-ratings";
import styles from "./product-tile.module.scss";
import ProductTileDialog from "./product-tile-dialog";

type Props = {
  product: any;
  sendEvent?: any;
  listScope?: string;
  noLazy?: boolean;
  isInCarousel?: boolean;
  imageLoaded?: () => void;
  algolia?: Algolia.ProductTileInfo;
};

export default function ProductTileAlgolia(props: Props) {
  const t = useTranslations();
  const { configuration } = useConfiguration();
  const hasChildren = props.product.children && Array.isArray(props.product.children);
  const [currentDialog, setCurrentDialog] = useState<"colors" | "notifyMe" | null>(null);
  const [source, setSource] = useState<CrossCountry.ChildrenProduct | CrossCountry.Product>(
    productUtils.getFirstAvailableProductOtherwiseGetFirstOOS(props.product, hasChildren)
  );
  const productUrl = props.product.slug + "-" + props.product.objectID;
  const _trackItem = (event: string, product: Algolia.Product, payload: Record<string, any> = {}) => {
    TrackingUtils.track(
      TrackingUtils.mapEcommerceEvent(
        event,
        [
          TrackingUtils.mapAlgoliaProduct(
            { ...product, currency: configuration.CurrencyID },
            {
              index: product.__position,
              item_list_name: props.listScope,
            }
          ),
        ],
        payload
      )
    );
  };

  return (
    <div className={styles.container}>
      <Link
        href={`/p/${productUrl}`}
        className={styles.container}
        onClick={() => {
          _trackItem("select_item", props.product);
          props.sendEvent?.("click", props.product, "Hit Clicked");
          props.algolia?.events?.clickEvent?.(props.algolia.product);
        }}
      >
        <div className={styles.image}>
          <NextImage
            priority
            src={
              (Array.isArray(props.product.images)
                ? props.product.images?.find((img: { name: string }) => img.name == "primary")?.url
                : props.product.images?.url) || imageUtils.transparentPixel
            }
            alt={props.product.name || ""}
            fill
            sizes={gridUtils.imageSizes}
            imageLoaded={props.isInCarousel ? props.imageLoaded : () => {}}
          />
        </div>
        <ProductListingTags product={props.product} className={styles.containerTags} keepContainer />
        <div className={styles.descriptionAlgolia}>
          <div className={styles.descAlgoliaClamp} dangerouslySetInnerHTML={{ __html: props.product.description }} />
        </div>
        <div className={styles.nameAlgolia}>
          <Highlight attribute="name" hit={props.product} className={styles.nameAlgoliaClamp} />
        </div>
        <div className={styles.ratings}>
          <ProductRatings average={props.product.averageRating || 0} numberOfRatings={props.product.reviewsNumber} />
        </div>
        <div className={styles.price}>
          {!props.isInCarousel && props.product.prices && (
            <ProductPriceAlgolia price={props.product.prices} isProductGrid />
          )}
          {props.isInCarousel && <ProductPrice product={props.product.prices} />}
        </div>
        <div className={styles.colorSelector}>
          {props.product.siblings?.length > 1 && (
            <div className={classNames(styles.searchShadesAvailables, styles.algoliaSearchShadesAvailables)}>
              <span>{t("generic.shadesavailables", { count: props.product.siblings.length })}</span>
              {/* <Icon name="chevron-right" width={16} height={16} aria-hidden /> */}
            </div>
          )}
        </div>
      </Link>
      {props.isInCarousel && props.product.prices && (
        <div className={styles.button}>
          <ProductActions
            hasPrice={productUtils.hasValidDisplayPrice(props.product.prices)}
            productId={props.product.product_id}
            maxQuantity={props.product.ecomm_max_items_per_order}
            outOfStock={!props.product?.has_stock}
            onClickNotifyMe={() => {
              if (productUtils.hasValidDisplayPrice(props.product)) {
                setCurrentDialog("notifyMe");
              }
            }}
            actionsContext={props.listScope}
            trackingData={TrackingUtils.mapEcomItem(props.product, props.product, {
              item_list_name: props.listScope,
            })}
            algolia={props.algolia}
          />
        </div>
      )}

      {!props.isInCarousel && (
        <Link
          href={`/p/${productUrl}`}
          className={styles.container}
          onClick={() => {
            _trackItem("select_item", props.product);
            props.sendEvent?.("click", props.product, "Hit Clicked");
            props.algolia?.events?.clickEvent?.(props.algolia.product);
          }}
        >
          <div className={classNames(styles.buttonView, buttonStyles.button, buttonStyles.primary)}>
            <span>{t("generic.viewDetails")}</span>
          </div>
        </Link>
      )}
      <ProductTileDialog
        {...{ currentDialog, setCurrentDialog, source, setSource, product: props.product, hasChildren }}
      />
    </div>
  );
}
